// Define the types for permit data
export interface Permit {
  permitType: string;
  name: string;
  workflowSteps: string[];
}

export interface PermitCategoryGroup {
  category: string;
  permits: Permit[];
}

const permitData: PermitCategoryGroup[] = [
  {
    category: 'Residential',
    permits: [
      { permitType: 'New Construction', name: 'Residential Building Permit', workflowSteps: [
          'Pre-application meeting (optional)',
          'Submit building plans and application',
          'Plan review by departments (building, electrical, plumbing, etc.)',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass foundation inspection',
          'Schedule and pass framing inspection',
          'Schedule and pass rough-in inspections (electrical, plumbing, HVAC)',
          'Schedule and pass insulation inspection',
          'Schedule and pass final inspections',
          'Obtain Certificate of Occupancy'
      ]},
      { permitType: 'Renovation', name: 'Residential Remodeling Permit', workflowSteps: [
          'Submit renovation plans and application',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspections (as applicable)',
          'Schedule and pass framing inspection (if applicable)',
          'Schedule and pass final inspections'
      ]},
      { permitType: 'Addition', name: 'Residential Addition Permit', workflowSteps: [
          'Submit addition plans and application',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass foundation inspection',
          'Schedule and pass framing inspection',
          'Schedule and pass rough-in inspections',
          'Schedule and pass final inspections'
      ]},
      { permitType: 'Accessory Structure', name: 'Accessory Building Permit', workflowSteps: [
          'Submit site plan and building plans',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass foundation inspection (if applicable)',
          'Schedule and pass framing inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Swimming Pool', name: 'Residential Pool/Spa Permit', workflowSteps: [
          'Submit pool plans and site plan',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass pre-gunite/pre-shotcrete inspection',
          'Schedule and pass bonding/grounding inspection',
          'Schedule and pass deck inspection',
          'Schedule and pass final inspection (including barrier/fence requirements)'
      ]},
      { permitType: 'Fence', name: 'Residential Fence Permit', workflowSteps: [
          'Submit application with fence location plan',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Roofing', name: 'Residential Re-roof Permit', workflowSteps: [
          'Submit application',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Plumbing', name: 'Residential Plumbing Permit', workflowSteps: [
          'Submit application',
          'Licensed plumber registration verification',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Electrical', name: 'Residential Electrical Permit', workflowSteps: [
          'Submit application',
          'Licensed electrician registration verification',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'HVAC', name: 'Residential Mechanical/HVAC Permit', workflowSteps: [
          'Submit application',
          'Licensed HVAC contractor registration verification',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Demolition', name: 'Residential Demolition Permit', workflowSteps: [
          'Submit application with site plan',
          'Utility disconnect verification',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass site clearance inspection'
      ]},
      { permitType: 'Driveway', name: 'Residential Driveway/Sidewalk Permit', workflowSteps: [
          'Submit application with site plan',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass form inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Water Heater', name: 'Water Heater Replacement Permit', workflowSteps: [
          'Submit application',
          'Licensed plumber verification (if applicable)',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass final inspection'
      ]}
    ]
  },
  {
    category: 'Commercial',
    permits: [
      { permitType: 'New Construction', name: 'Commercial Building Permit', workflowSteps: [
          'Pre-application meeting',
          'Submit building plans and application',
          'Plan review by departments (building, fire, engineering, etc.)',
          'Address plan review comments',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass foundation inspection',
          'Schedule and pass framing/structural inspections',
          'Schedule and pass rough-in inspections',
          'Schedule and pass fire safety inspections',
          'Schedule and pass final inspections',
          'Obtain Certificate of Occupancy'
      ]},
      { permitType: 'Interior Finish', name: 'Commercial Tenant Improvement/Finish-Out Permit', workflowSteps: [
          'Submit finish-out plans and application',
          'Plan review',
          'Address plan review comments',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass framing/structural inspections (if applicable)',
          'Schedule and pass rough-in inspections',
          'Schedule and pass fire safety inspections (if applicable)',
          'Schedule and pass final inspections',
          'Obtain Certificate of Occupancy'
      ]},
      { permitType: 'Change of Use', name: 'Certificate of Occupancy/Change of Use Permit', workflowSteps: [
          'Submit application with floor plan',
          'Plan review for zoning compliance',
          'Building inspection',
          'Fire inspection (if applicable)',
          'Pay permit fees',
          'Certificate issuance'
      ]},
      { permitType: 'Renovation', name: 'Commercial Remodeling Permit', workflowSteps: [
          'Submit remodeling plans and application',
          'Plan review',
          'Address plan review comments',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspections',
          'Schedule and pass framing/structural inspections (if applicable)',
          'Schedule and pass fire safety inspections (if applicable)',
          'Schedule and pass final inspections'
      ]},
      { permitType: 'Signage', name: 'Commercial Sign Permit', workflowSteps: [
          'Submit sign plans and application',
          'Zoning review',
          'Building/structural review (if applicable)',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Parking', name: 'Commercial Parking Lot Permit', workflowSteps: [
          'Submit site plan with parking layout',
          'Engineering review',
          'Drainage review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass drainage inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Plumbing', name: 'Commercial Plumbing Permit', workflowSteps: [
          'Submit plumbing plans and application',
          'Licensed plumber registration verification',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Electrical', name: 'Commercial Electrical Permit', workflowSteps: [
          'Submit electrical plans and application',
          'Licensed electrician registration verification',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'HVAC', name: 'Commercial Mechanical/HVAC Permit', workflowSteps: [
          'Submit mechanical plans and application',
          'Licensed HVAC contractor registration verification',
          'Plan review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspection',
          'Schedule and pass final inspection'
      ]},
      { permitType: 'Fire Safety', name: 'Fire Alarm/Sprinkler System Permit', workflowSteps: [
          'Submit fire protection plans and application',
          'Licensed fire protection contractor verification',
          'Fire department review',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass rough-in inspection',
          'Schedule and pass final inspection/testing'
      ]},
      { permitType: 'Demolition', name: 'Commercial Demolition Permit', workflowSteps: [
          'Submit application with site plan',
          'Asbestos survey submission (if applicable)',
          'Utility disconnect verification',
          'Pay permit fees',
          'Permit issuance',
          'Schedule and pass site clearance inspection'
      ]}
    ]
  },
  {
  category: 'Industrial',
  permits: [
    {
      permitType: 'New Construction',
      name: 'Industrial Building Permit',
      workflowSteps: [
        'Pre-application meeting',
        'Submit building plans and application',
        'Plan review by departments (building, fire, engineering, environmental)',
        'Address plan review comments',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass foundation inspection',
        'Schedule and pass structural inspections',
        'Schedule and pass rough-in inspections',
        'Schedule and pass fire safety inspections',
        'Schedule and pass environmental compliance inspections (if applicable)',
        'Schedule and pass final inspections',
        'Obtain Certificate of Occupancy'
      ]
    },
    {
      permitType: 'Storage Tanks',
      name: 'Storage Tank Installation Permit',
      workflowSteps: [
        'Submit tank specifications and site plans',
        'Fire marshal review',
        'Environmental review',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass installation inspection',
        'Schedule and pass pressure/leak testing',
        'Schedule and pass final inspection'
      ]
    },
    {
      permitType: 'Hazardous Materials',
      name: 'Hazardous Materials Handling Permit',
      workflowSteps: [
        'Submit hazardous materials inventory statement',
        'Submit hazardous materials management plan',
        'Fire department review',
        'Environmental review',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass facility inspection',
        'Annual renewal with updated inventory'
      ]
    },
    {
      permitType: 'Wastewater',
      name: 'Industrial Wastewater Discharge Permit',
      workflowSteps: [
        'Submit wastewater discharge application',
        'Submit process flow diagrams',
        'Laboratory analysis of discharge',
        'Environmental review',
        'Pay permit fees',
        'Permit issuance',
        'Periodic sampling and reporting',
        'Annual renewal'
      ]
    },
    {
      permitType: 'Equipment',
      name: 'Industrial Equipment Installation Permit',
      workflowSteps: [
        'Submit equipment specifications and plans',
        'Structural review (if applicable)',
        'Environmental review (emissions/noise)',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass installation inspection',
        'Schedule and pass operational testing',
        'Schedule and pass final inspection'
      ]
    },
    {
      permitType: 'Storage',
      name: 'High-Pile Storage Permit',
      workflowSteps: [
        'Submit storage plans and commodity information',
        'Fire department review',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass pre-occupancy inspection',
        'Annual renewal with inspection'
      ]
    },
    {
      permitType: 'Fire Safety',
      name: 'Industrial Fire Suppression System Permit',
      workflowSteps: [
        'Submit fire protection plans',
        'Licensed fire protection contractor verification',
        'Fire department review',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass rough-in inspection',
        'Schedule and pass hydrostatic testing (if applicable)',
        'Schedule and pass final inspection/testing'
      ]
    },
    {
      permitType: 'Environmental',
      name: 'Environmental Compliance Permit',
      workflowSteps: [
        'Submit environmental impact assessment',
        'Submit pollution control measures',
        'Environmental review',
        'Public notice period (if applicable)',
        'Pay permit fees',
        'Permit issuance',
        'Periodic compliance reporting',
        'Scheduled and unscheduled compliance inspections'
      ]
    },
    {
      permitType: 'Loading Infrastructure',
      name: 'Loading Dock Construction Permit',
      workflowSteps: [
        'Submit loading dock plans',
        'Engineering review',
        'Traffic impact review',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass foundation inspection',
        'Schedule and pass structural inspection',
        'Schedule and pass final inspection'
      ]
    }
  ]
},
{
  category: 'General',
  permits: [
    {
      permitType: 'Land Development',
      name: 'Land Development/Site Development Permit',
      workflowSteps: [
        'Pre-application meeting',
        'Submit site development plans',
        'Engineering review',
        'Drainage review',
        'Traffic review',
        'Environmental review',
        'Address plan review comments',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass rough grading inspection',
        'Schedule and pass utility installation inspections',
        'Schedule and pass final grading inspection',
        'Schedule and pass final site inspection'
      ]
    },
    {
      permitType: 'Earthwork',
      name: 'Grading/Excavation Permit',
      workflowSteps: [
        'Submit grading plan',
        'Submit erosion control plan',
        'Engineering review',
        'Environmental review',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass pre-construction inspection',
        'Schedule and pass erosion control inspection',
        'Schedule and pass final grading inspection'
      ]
    },
    {
      permitType: 'Tree Removal',
      name: 'Tree Removal/Preservation Permit',
      workflowSteps: [
        'Submit tree survey',
        'Submit tree preservation/replacement plan',
        'Arborist/environmental review',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass pre-removal inspection',
        'Schedule and pass replacement inspection (if applicable)'
      ]
    },
    {
      permitType: 'Floodplain',
      name: 'Floodplain Development Permit',
      workflowSteps: [
        'Submit floodplain development application',
        'Submit elevation certificate',
        'Submit hydraulic/hydrologic analysis (if applicable)',
        'Engineering review',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass foundation elevation inspection',
        'Schedule and pass final inspection',
        'Submit final elevation certificate'
      ]
    },
    {
      permitType: 'Temporary Use',
      name: 'Temporary Use/Special Event Permit',
      workflowSteps: [
        'Submit temporary use application',
        'Submit site plan',
        'Zoning review',
        'Building safety review',
        'Fire safety review (if applicable)',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass setup inspection (if applicable)',
        'Schedule and pass final inspection (if applicable)'
      ]
    },
    {
      permitType: 'Public Space',
      name: 'Right-of-Way Permit',
      workflowSteps: [
        'Submit right-of-way application',
        'Submit traffic control plan (if applicable)',
        'Engineering review',
        'Utility coordination',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass pre-construction inspection',
        'Schedule and pass utility installation inspection (if applicable)',
        'Schedule and pass final restoration inspection'
      ]
    },
    {
      permitType: 'Irrigation',
      name: 'Irrigation/Sprinkler System Permit',
      workflowSteps: [
        'Submit irrigation plans',
        'Licensed irrigator verification',
        'Backflow prevention certification',
        'Pay permit fees',
        'Permit issuance',
        'Schedule and pass installation inspection',
        'Schedule and pass backflow inspection',
        'Schedule and pass final inspection'
      ]
    },
    {
      permitType: 'Occupancy',
      name: 'Certificate of Occupancy',
      workflowSteps: [
        'Submit application with floor plan',
        'Building inspection',
        'Fire inspection',
        'Health inspection (if applicable)',
        'Pay fees',
        'Certificate issuance'
      ]
    },
    {
      permitType: 'Variance',
      name: 'Zoning Variance Request',
      workflowSteps: [
        'Submit variance application',
        'Submit property survey and site plan',
        'Submit hardship justification',
        'Staff review',
        'Public notice to adjacent property owners',
        'Pay application fees',
        'Public hearing with zoning board/commission',
        'Board decision',
        'If approved, proceed with related permits'
      ]
    },
    {
      permitType: 'Event',
      name: 'Special Event Permit',
      workflowSteps: [
        'Submit special event application',
        'Submit site/event layout',
        'Submit security/emergency plan (if applicable)',
        'Department reviews (police, fire, health, traffic)',
        'Pay permit fees',
        'Permit issuance',
        'Pre-event inspection (if applicable)',
        'Post-event inspection (if applicable)'
      ]
    },
    {
      permitType: 'Contractor',
      name: 'Contractor Registration',
      workflowSteps: [
        'Submit registration application',
        'Submit proof of insurance',
        'Submit proof of state license (if applicable)',
        'Pay registration fee',
        'Registration issuance',
        'Annual renewal'
      ]
    }
  ]
}

];

export default permitData;
